import api from './api';
import { tasksApi, type Task, type TaskCreatePayload } from './tasks';

export type MeetlyMeetingStatus = 'SUBMITTING' | 'QUEUED' | 'COMPLETED' | 'FAILED';
export type MeetlyMeetingStatusRaw = MeetlyMeetingStatus
  | 'PROCESSING'
  | 'TRANSCRIBING'
  | 'PROCESSING_AUDIO'
  | 'SUMMARIZING'
  | 'GENERATING_SUMMARY'
  | 'UPLOADING'
  | 'AUDIO_PROCESSING_FAILED'
  | 'RECORDING_FAILED'
  | 'UPLOAD_FAILED'
  | 'TRANSCRIPTION_FAILED'
  | 'SUMMARY_FAILED'
  | 'UPLOADED'
  | 'RECORDING';

export const normalizeMeetlyMeetingStatus = (status: MeetlyMeetingStatusRaw): MeetlyMeetingStatus => {
  if (status === 'FAILED'
    || status === 'AUDIO_PROCESSING_FAILED'
    || status === 'RECORDING_FAILED'
    || status === 'UPLOAD_FAILED'
    || status === 'TRANSCRIPTION_FAILED'
    || status === 'SUMMARY_FAILED') {
    return 'FAILED';
  }

  if (status === 'SUBMITTING'
    || status === 'UPLOADING'
    || status === 'UPLOADED'
    || status === 'RECORDING') {
    return 'SUBMITTING';
  }

  if (status === 'PROCESSING'
    || status === 'PROCESSING_AUDIO'
    || status === 'TRANSCRIBING'
    || status === 'SUMMARIZING'
    || status === 'GENERATING_SUMMARY'
    || status === 'QUEUED') {
    return 'QUEUED';
  }

  return status;
};

export interface MeetlyActionItem {
  id: string;
  text: string;
  owner?: string | null;
  task?: string | null;
  due?: string | null;
  suggested_assignee: string | null;
  suggested_due_date: string | null;
  segment_timestamp?: string | null;
  order_index: number;
  created_task_id: string | null;
  task_created: boolean;
}

export interface MeetlyContextNote {
  id: string;
  text: string;
  created_at: string;
  updated_at: string;
}

export interface MeetlyMeeting {
  id: string;
  title: string | null;
  status: MeetlyMeetingStatusRaw;
  summary: string | null;
  action_items: MeetlyActionItem[];
  error: string | null;
  language: string | null;
  summary_template: string | null;
  context_notes: MeetlyContextNote[];
  duration_seconds: number | null;
  capture_mode: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

// The summariser never produces a bespoke meeting title, so the closest
// thing to a real name is the template it was run with.
const SUMMARY_TEMPLATE_LABELS: Record<string, string> = {
  project_sync: 'Project sync meeting',
  daily_standup: 'Daily standup meeting',
  client_call: 'Client call meeting',
  general: 'General meeting',
};

export const meetlyMeetingTitle = (meeting: MeetlyMeeting) =>
  meeting.title?.trim()
  || SUMMARY_TEMPLATE_LABELS[meeting.summary_template || '']
  || 'Meeting';

export const CAPTURE_MODE_LABELS: Record<string, string> = {
  microphone: 'Microphone only',
  'microphone-and-system': 'Microphone + system audio',
  imported: 'Imported audio file',
};

export interface MeetlyMeetingResponse {
  meeting: MeetlyMeeting;
}

export interface MeetlyJobSubmissionResponse {
  job_id: string;
  meeting_id: string;
  accepted: boolean;
  status?: string;
}

export interface MeetlyTranscriptResponse {
  meeting_id: string;
  transcript: string;
}

export interface MeetlyMeetingUpdate {
  title: string;
  summary: string;
  action_items: Array<{
    id?: string;
    text: string;
    owner?: string | null;
    task?: string | null;
    due?: string | null;
  }>;
}

export interface MeetlyStandardResponse {
  success: boolean;
  message: string;
  data: Record<string, unknown>;
  errorCode: string | null;
  processingStatus: string | null;
  progress: Record<string, unknown> | null;
  retryable: boolean;
}

export interface MeetlyMeetingListResponse {
  meetings: MeetlyMeeting[];
  total: number;
  page: number;
  page_size: number;
}

export interface MeetlyActionItemTaskResponse {
  action_item: MeetlyActionItem;
  task: Task;
}

export interface MeetlyCredits { ok: boolean; limit: number; used: number; reserved: number; remaining: number; source: string; locked_by_admin: boolean; period_start: string; period_end: string; resets_on: string; }
export interface MeetlyUsageTotals { prompt_tokens: number; completion_tokens: number; total_tokens: number; calls: number; failed: number; messages: number; }
export interface MeetlyUsage { scope: 'self' | 'team' | 'branch'; start: string; end: string; totals: MeetlyUsageTotals; by_day: Array<{ date: string } & MeetlyUsageTotals>; people: Array<{ user_id: string; name: string } & MeetlyUsageTotals>; }
export interface MeetlyLimits { scope: 'self' | 'team' | 'branch'; self: MeetlyCredits; people: Array<{ user_id: string; monthly_credits: number; locked_by_admin: boolean; can_edit: boolean; }>; }

export const getMeetlyCredits = () => api.get<MeetlyCredits>('/meetly/credits');
export const getMeetlyUsage = (start?: string, end?: string) => api.get<MeetlyUsage>('/meetly/usage', { params: { start, end } });
export const getMeetlyLimits = () => api.get<MeetlyLimits>('/meetly/limits');
export const setMeetlyLimit = (userId: string, monthlyCredits: number) => api.put(`/meetly/limits/${encodeURIComponent(userId)}`, { monthly_credits: monthlyCredits });

const newMeetingId = () => {
  const randomId = globalThis.crypto?.randomUUID?.();
  return `meeting-${randomId || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`}`;
};

export const meetlyApi = {
  createMeeting: (
    file: File,
    options: {
      language?: string;
      summary_template?: string;
      duration_seconds?: number;
      capture_mode?: string;
    } = {},
  ) => {
    const formData = new FormData();
    formData.append('meeting_id', newMeetingId());
    formData.append('file', file);
    formData.append('language', options.language || 'en');
    formData.append('summary_template', options.summary_template || 'project_sync');
    if (options.duration_seconds !== undefined) {
      formData.append('duration_seconds', String(options.duration_seconds));
    }
    if (options.capture_mode) {
      formData.append('capture_mode', options.capture_mode);
    }
    return api.post<MeetlyJobSubmissionResponse>('/meetly/jobs', formData, {
      // Meeting captures can be much larger than ordinary JSON requests. Let
      // the request complete instead of surfacing a false 30-second failure
      // while Meetly continues processing the same upload server-side.
      timeout: 0,
    });
  },

  listMeetings: (params: { page?: number; page_size?: number } = {}) =>
    api.get<MeetlyMeetingListResponse>('/meetly/meetings', { params }),

  getMeeting: (meetingId: string) =>
    api.get<MeetlyMeetingResponse>(`/meetly/meetings/${encodeURIComponent(meetingId)}`),

  getTranscript: (meetingId: string) =>
    api.get<MeetlyTranscriptResponse>(`/meetly/meetings/${encodeURIComponent(meetingId)}/transcript`),

  updateMeeting: (meetingId: string, payload: MeetlyMeetingUpdate) =>
    api.put<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}`,
      {
        title: payload.title,
        summary: payload.summary,
        action_items: payload.action_items.map(({ id, text, owner, task, due }) => ({
          id,
          text,
          owner,
          task,
          due,
        })),
      },
    ),

  deleteMeeting: (meetingId: string) =>
    api.delete<{ success: true }>(`/meetly/meetings/${encodeURIComponent(meetingId)}`),

  // Kicks off regeneration and returns a job envelope, not the finished
  // meeting — the caller re-polls getMeeting for the new summary.
  regenerateSummary: (
    meetingId: string,
    options: { language?: string; summary_template?: string } = {},
  ) =>
    api.post<MeetlyStandardResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/regenerate`,
      options,
    ),

  addContextNote: (meetingId: string, text: string) =>
    api.post<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/context`,
      { text },
    ),

  updateContextNote: (meetingId: string, noteId: string, text: string) =>
    api.put<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/context/${encodeURIComponent(noteId)}`,
      { text },
    ),

  deleteContextNote: (meetingId: string, noteId: string) =>
    api.delete<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/context/${encodeURIComponent(noteId)}`,
    ),

  // Lio-Notes owns meeting data; creating a Teamlio task uses the ordinary
  // authenticated task endpoint and never needs an internal Meetly route.
  createTaskFromActionItem: (
    _meetingId: string,
    actionItemId: string,
    task: TaskCreatePayload,
  ) => tasksApi.create(task).then(response => ({
    ...response,
    data: {
      task: response.data as Task,
      action_item: { id: actionItemId } as MeetlyActionItem,
    },
  })),
};

export default meetlyApi;
